import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  useSystemSettings,
  useNotifications,
  useIntegrationStatus,
  useUpdateProfile,
  useChangePassword,
  useUsers,
} from '../hooks/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Avatar from './Avatar';
import {
  User,
  Camera,
  Lock,
  Mail,
  Phone,
  Building,
  Save,
  Bell,
  Settings,
  Shield,
  Calendar,
  Activity,
  Link2,
  Play,
  Loader2,
  Sun,
  Moon,
  Monitor,
  MessageSquare,
  ChevronRight,
  ExternalLink,
  Eye,
  EyeOff,
  Check,
  Palette,
  Plus,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { toUTCPlus5 } from '../utils/time';
import GitHubService from '../services/github';
import { GitHubIcon, OutlookIcon } from './icons/BrandIcons';
import SpotifyIcon from './spotify/SpotifyIcon';
import {
  SYSTEM_SETTINGS_DEFAULTS,
  DAY_LABELS,
  mergeSystemSettings,
} from '../constants/systemSettingsDefaults';
import {
  formatNextRunPreview,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
  REMINDER_LEVEL_OPTIONS,
} from '../utils/weeklyHoursSchedule';

const SECTIONS = {
  profile: { label: 'Profile', icon: User, group: 'account' },
  security: { label: 'Security', icon: Shield, group: 'account' },
  integrations: { label: 'Connected Apps', icon: Link2, group: 'preferences' },
  appearance: { label: 'Appearance', icon: Palette, group: 'preferences' },
  notifications: { label: 'Notifications', icon: Bell, group: 'preferences' },
  system: { label: 'System Config', icon: Settings, group: 'admin' },
};

const ProfilePage = () => {
  const { user, setUser, fetchUser, loading: authLoading } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // /api/users/{id} is admin-only; Profile already has user from AuthContext
  const { data: systemSettingsData } = useSystemSettings();
  const queryClient = useQueryClient();
  const { data: notificationsData } = useNotifications();
  const { data: integrationStatusData } = useIntegrationStatus();
  const { data: allUsersData } = useUsers();
  const allUsers = Array.isArray(allUsersData) ? allUsersData : (allUsersData?.users || []);
  const availableDepartments = useMemo(() => {
    const names = new Set();
    allUsers.forEach((u) => {
      const d = (u.department || '').trim();
      if (d) names.add(d);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [allUsers]);

  /** Same pool as backend member pace reminders (members + project leads, active). */
  const reminderEligibleUsers = useMemo(() => {
    return allUsers.filter((u) => {
      const role = u.role;
      if (role !== 'member' && role !== 'project_lead') return false;
      if (u.is_disabled) return false;
      if (u.is_deleted) return false;
      if (u.invite_token) return false;
      if (u.is_verified === false) return false;
      const approval = u.approval_status;
      if (approval && approval !== 'approved') return false;
      return true;
    });
  }, [allUsers]);

  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();

  const notifications = notificationsData || [];
  const unreadCount = notifications.filter(n => !n.is_read).length;
  const isAdmin = user?.role === 'admin';

  const initialTab = searchParams.get('tab') || 'profile';
  const [activeSection, setActiveSection] = useState(
    Object.keys(SECTIONS).includes(initialTab) ? initialTab : 'profile'
  );

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setSearchParams({ tab: section }, { replace: true });
  };

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && Object.keys(SECTIONS).includes(tab)) {
      setActiveSection(tab);
    }
  }, [searchParams]);

  const [loading, setLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    department: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [savingConfig, setSavingConfig] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [systemConfig, setSystemConfig] = useState(() => ({
    ...SYSTEM_SETTINGS_DEFAULTS,
  }));
  const memberReminderRecipientPreview = useMemo(() => {
    const selected = systemConfig.member_reminder_departments || [];
    const excluded = new Set(systemConfig.member_reminder_excluded_user_ids || []);
    const totalEligible = reminderEligibleUsers.length;
    if (!selected.length) {
      return { filtered: false, count: totalEligible, totalEligible, pool: reminderEligibleUsers };
    }
    const allowed = new Set(
      selected.map((d) => String(d).trim().toLowerCase()).filter(Boolean)
    );
    const pool = reminderEligibleUsers.filter((u) =>
      allowed.has(String(u.department || '').trim().toLowerCase())
    );
    const count = pool.filter((u) => !excluded.has(u.id)).length;
    return { filtered: true, count, totalEligible, pool };
  }, [
    reminderEligibleUsers,
    systemConfig.member_reminder_departments,
    systemConfig.member_reminder_excluded_user_ids,
  ]);
  const hrReportRecipientPreview = useMemo(() => {
    const selected = systemConfig.hr_report_departments || [];
    const excluded = new Set(systemConfig.hr_report_excluded_user_ids || []);
    const totalEligible = reminderEligibleUsers.length;
    if (!selected.length) {
      return { filtered: false, count: totalEligible, totalEligible, pool: reminderEligibleUsers };
    }
    const allowed = new Set(
      selected.map((d) => String(d).trim().toLowerCase()).filter(Boolean)
    );
    const pool = reminderEligibleUsers.filter((u) =>
      allowed.has(String(u.department || '').trim().toLowerCase())
    );
    const count = pool.filter((u) => !excluded.has(u.id)).length;
    return { filtered: true, count, totalEligible, pool };
  }, [
    reminderEligibleUsers,
    systemConfig.hr_report_departments,
    systemConfig.hr_report_excluded_user_ids,
  ]);
  const [hrPickerOpen, setHrPickerOpen] = useState(false);
  const [hrSearch, setHrSearch] = useState('');
  const [triggeringJob, setTriggeringJob] = useState(null);
  const [holidayDraft, setHolidayDraft] = useState({ date: '', name: '' });
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [githubConnected, setGithubConnected] = useState(false);
  const [githubConfigured, setGithubConfigured] = useState(false);
  const [microsoftCalendarConnected, setMicrosoftCalendarConnected] = useState(false);
  const [meetingTimeTrackingEnabled, setMeetingTimeTrackingEnabled] = useState(true);
  const [meetingTrackingLoading, setMeetingTrackingLoading] = useState(false);
  const [triggerMeetingTrackingLoading, setTriggerMeetingTrackingLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        department: user.department || ''
      });
    }
  }, [user]);

  useEffect(() => {
    checkIntegrationStatus();
  }, []);

  const checkIntegrationStatus = async () => {
    try {
      const githubStatus = await GitHubService.getConnectionStatus();
      setGithubConnected(githubStatus.connected);
      setGithubConfigured(githubStatus.app_configured);

      try {
        const token = localStorage.getItem('token');
        const calendarResponse = await axios.get('/api/calendar/status', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (calendarResponse.data.success) {
          setMicrosoftCalendarConnected(calendarResponse.data.data.connected);
        } else {
          setMicrosoftCalendarConnected(false);
        }
      } catch {
        setMicrosoftCalendarConnected(false);
      }

      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/users/meeting-tracking-status', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setMeetingTimeTrackingEnabled(res.data.meeting_time_tracking_enabled);
      } catch {
        setMeetingTimeTrackingEnabled(true);
      }
    } catch (error) {
      console.error('Error checking integration status:', error);
    }
  };

  const setConfigField = (key, value) => {
    setSystemConfig((prev) => ({ ...prev, [key]: value }));
  };

  const toggleWorkingDay = (day) => {
    setSystemConfig((prev) => {
      const days = new Set(prev.working_days || []);
      if (days.has(day)) {
        if (days.size <= 1) return prev;
        days.delete(day);
      } else {
        days.add(day);
      }
      return { ...prev, working_days: Array.from(days).sort((a, b) => a - b) };
    });
  };

  const toggleReminderDay = (day) => {
    setSystemConfig((prev) => {
      const days = new Set(prev.member_reminder_days || []);
      if (days.has(day)) {
        if (days.size <= 1) return prev;
        days.delete(day);
      } else {
        days.add(day);
      }
      return {
        ...prev,
        member_reminder_days: Array.from(days).sort((a, b) => a - b),
      };
    });
  };

  const toggleReminderDepartment = (dept) => {
    setSystemConfig((prev) => {
      const selected = new Set(prev.member_reminder_departments || []);
      if (selected.has(dept)) {
        selected.delete(dept);
      } else {
        selected.add(dept);
      }
      const nextDepts = Array.from(selected).sort((a, b) => a.localeCompare(b));
      const allowed = new Set(nextDepts.map((d) => d.toLowerCase()));
      const remainingIds = new Set(
        reminderEligibleUsers
          .filter((u) => allowed.has(String(u.department || '').trim().toLowerCase()))
          .map((u) => u.id)
      );
      const nextExcluded = (prev.member_reminder_excluded_user_ids || []).filter((id) =>
        remainingIds.has(id)
      );
      return {
        ...prev,
        member_reminder_departments: nextDepts,
        member_reminder_excluded_user_ids: nextDepts.length ? nextExcluded : [],
      };
    });
  };

  const toggleHrReportDepartment = (dept) => {
    setSystemConfig((prev) => {
      const selected = new Set(prev.hr_report_departments || []);
      if (selected.has(dept)) {
        selected.delete(dept);
      } else {
        selected.add(dept);
      }
      const nextDepts = Array.from(selected).sort((a, b) => a.localeCompare(b));
      const allowed = new Set(nextDepts.map((d) => d.toLowerCase()));
      const remainingIds = new Set(
        reminderEligibleUsers
          .filter((u) => allowed.has(String(u.department || '').trim().toLowerCase()))
          .map((u) => u.id)
      );
      const nextExcluded = (prev.hr_report_excluded_user_ids || []).filter((id) =>
        remainingIds.has(id)
      );
      return {
        ...prev,
        hr_report_departments: nextDepts,
        hr_report_excluded_user_ids: nextDepts.length ? nextExcluded : [],
      };
    });
  };

  const toggleAudienceUser = (excludedKey, userId) => {
    setSystemConfig((prev) => {
      const excluded = new Set(prev[excludedKey] || []);
      if (excluded.has(userId)) {
        excluded.delete(userId);
      } else {
        excluded.add(userId);
      }
      return { ...prev, [excludedKey]: Array.from(excluded) };
    });
  };

  const setAudienceUsersSelected = (excludedKey, pool, selectAll) => {
    setSystemConfig((prev) => ({
      ...prev,
      [excludedKey]: selectAll ? [] : pool.map((u) => u.id).filter(Boolean),
    }));
  };

  const moveReminderLevel = (index, direction) => {
    setSystemConfig((prev) => {
      const levels = [...(prev.member_reminder_levels || [])];
      const target = index + direction;
      if (target < 0 || target >= levels.length) return prev;
      [levels[index], levels[target]] = [levels[target], levels[index]];
      return { ...prev, member_reminder_levels: levels };
    });
  };

  const addCompanyHoliday = () => {
    const date = (holidayDraft.date || '').trim();
    if (!date) {
      toast.error('Pick a holiday date');
      return;
    }
    const existing = systemConfig.company_holidays || [];
    if (existing.some((h) => h.date === date)) {
      toast.error('That date is already listed');
      return;
    }
    setSystemConfig((prev) => {
      const next = [
        ...(prev.company_holidays || []),
        { date, name: (holidayDraft.name || '').trim() },
      ].sort((a, b) => a.date.localeCompare(b.date));
      return { ...prev, company_holidays: next };
    });
    setHolidayDraft({ date: '', name: '' });
  };

  const removeCompanyHoliday = (date) => {
    setSystemConfig((prev) => ({
      ...prev,
      company_holidays: (prev.company_holidays || []).filter((h) => h.date !== date),
    }));
  };

  const triggerWeeklyJob = async (endpoint, label) => {
    setTriggeringJob(endpoint);
    try {
      await axios.post(`/api/monitoring/emails/${endpoint}`);
      toast.success(`${label} started`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to trigger ${label}`);
    } finally {
      setTriggeringJob(null);
    }
  };

  const fetchSystemSettings = async () => {
    setLoadingConfig(true);
    try {
      const response = await axios.get('/api/system-settings/');
      const merged = mergeSystemSettings(response.data);
      setSystemConfig({
        ...merged,
        ai_prompt_template:
          response.data.ai_prompt_template ||
          response.data.openai_prompt_template ||
          '',
        ai_model:
          response.data.ai_model || response.data.openai_model || 'gpt-4o-mini',
        office_name: response.data.office_name || '',
        office_latitude: response.data.office_latitude ?? 24.8607,
        office_longitude: response.data.office_longitude ?? 67.0011,
        office_radius_km: response.data.office_radius_km ?? 1,
      });
    } catch {
      toast.error('Failed to load system settings');
    } finally {
      setLoadingConfig(false);
    }
  };

  useEffect(() => {
    if (isAdmin && activeSection === 'system') {
      fetchSystemSettings();
    }
  }, [activeSection, isAdmin]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateProfileMutation.mutateAsync(profileData);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await changePasswordMutation.mutateAsync({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    if (!user) { toast.error('User not authenticated'); return; }
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PNG, JPG, and JPEG images are allowed');
      return;
    }
    setAvatarUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post('/api/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data?.avatar_url) {
        setUser(prev => ({ ...prev, avatar: response.data.avatar_url }));
        toast.success('Avatar updated successfully');
        setTimeout(() => fetchUser(), 100);
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to update avatar');
    } finally {
      setAvatarUploading(false);
      e.target.value = '';
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`/api/notifications/${notificationId}/read`);
      window.location.reload();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleGenerateBackupCodes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/2fa/backup-codes');
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodes(true);
      toast.success('Backup codes generated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate backup codes');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async (verificationCode) => {
    try {
      setLoading(true);
      await axios.post('/api/2fa/disable', { code: verificationCode });
      toast.success('2FA disabled successfully');
      setShowDisable2FA(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGitHub = async () => {
    try {
      setLoading(true);
      const isConfigured = await GitHubService.isAppConfigured();
      if (!isConfigured) {
        toast.error('GitHub OAuth is not configured. Please contact your administrator.');
        return;
      }
      localStorage.setItem('github_oauth_return_tab', activeSection);
      localStorage.removeItem('github_oauth_state');
      sessionStorage.removeItem('github_oauth_in_progress');
      const result = await GitHubService.installApp();
      if (result.success) {
        toast.success('Redirecting to GitHub for authorization...');
      }
    } catch (error) {
      toast.error('Failed to start GitHub OAuth connection: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectGitHub = async () => {
    try {
      await GitHubService.disconnectGitHubOAuth();
      setGithubConnected(false);
      toast.success('GitHub disconnected successfully');
      checkIntegrationStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to disconnect GitHub');
    }
  };

  const handleDisconnectMicrosoft = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete('/api/calendar/disconnect', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.success) {
        setMicrosoftCalendarConnected(false);
        toast.success('Microsoft Calendar disconnected');
        checkIntegrationStatus();
      }
    } catch {
      toast.error('Failed to disconnect Microsoft Calendar');
    }
  };

  const handleToggleMeetingTimeTracking = async (enabled) => {
    try {
      setMeetingTrackingLoading(true);
      const token = localStorage.getItem('token');
      await axios.post('/api/users/meeting-time-tracking',
        { enabled },
        { headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setMeetingTimeTrackingEnabled(enabled);
      toast.success(`Meeting time tracking ${enabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update meeting time tracking');
    } finally {
      setMeetingTrackingLoading(false);
    }
  };

  const handleTriggerMeetingTracking = async () => {
    try {
      setTriggerMeetingTrackingLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/users/trigger-meeting-tracking', {}, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      toast.success(`Created ${response.data.created_entries} time entries from meetings`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to trigger meeting tracking');
    } finally {
      setTriggerMeetingTrackingLoading(false);
    }
  };

  const visibleSections = Object.entries(SECTIONS).filter(([key, s]) => {
    if (s.group === 'admin' && !isAdmin) return false;
    return true;
  });

  const sectionGroups = [
    { label: 'Account', keys: visibleSections.filter(([, s]) => s.group === 'account') },
    { label: 'Preferences', keys: visibleSections.filter(([, s]) => s.group === 'preferences') },
    ...(isAdmin ? [{ label: 'Administration', keys: visibleSections.filter(([, s]) => s.group === 'admin') }] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="card">
        <div className="flex flex-col sm:flex-row items-center gap-5">
          <div className="relative group flex-shrink-0">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden ring-2 ring-primary-200 dark:ring-primary-700">
              <Avatar
                blobName={user?.avatar}
                alt={user?.full_name || user?.username || 'User'}
                size="2xl"
                className="w-16 h-16 sm:w-20 sm:h-20"
                fallbackText={user?.full_name ? user.full_name[0] : user?.username ? user.username[0] : 'U'}
                previewable
              />
              {avatarUploading && (
                <div className="absolute inset-0 bg-secondary-900/50 flex items-center justify-center rounded-full">
                  <Loader2 className="h-5 w-5 animate-spin text-white" />
                </div>
              )}
            </div>
            <label className="absolute -bottom-1 -right-1 p-1.5 bg-primary-600 hover:bg-primary-700 rounded-full shadow-sm cursor-pointer transition-colors">
              <Camera className="h-3 w-3 text-white" />
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleAvatarUpload}
                className="hidden"
                disabled={avatarUploading}
              />
            </label>
          </div>
          <div className="text-center sm:text-left flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white truncate">{user?.full_name || user?.username}</h2>
            <p className="text-sm text-secondary-500 dark:text-secondary-400 truncate">{user?.email}</p>
            <div className="flex items-center gap-2 mt-2 justify-center sm:justify-start flex-wrap">
              <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-medium rounded-full">
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
              </span>
              {user?.department && (
                <span className="px-2 py-0.5 bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300 text-xs rounded-full">
                  {user.department}
                </span>
              )}
              {user?.two_factor_enabled && (
                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs rounded-full flex items-center gap-1">
                  <Shield className="h-3 w-3" /> 2FA
                </span>
              )}
            </div>
          </div>
          {/* Quick Actions */}
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => navigate('/my-activity')}
              className="btn-secondary flex items-center gap-1.5 text-xs px-3 py-1.5"
            >
              <Activity className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">My Activity</span>
            </button>
            {isAdmin && (
              <button
                onClick={() => navigate('/admin/chat-history')}
                className="btn-secondary flex items-center gap-1.5 text-xs px-3 py-1.5"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Chat History</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content: Sidebar + Content */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar Nav (desktop) / Horizontal pills (mobile) */}
        <nav className="lg:w-56 flex-shrink-0">
          {/* Mobile: horizontal scroll pills */}
          <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
            {visibleSections.map(([key, section]) => {
              const Icon = section.icon;
              const isActive = activeSection === key;
              return (
                <button
                  key={key}
                  onClick={() => handleSectionChange(key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                    isActive
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'bg-white dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 border border-secondary-200 dark:border-secondary-700 hover:border-primary-300 dark:hover:border-primary-600'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {section.label}
                  {key === 'notifications' && unreadCount > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full ${isActive ? 'bg-white/20 text-white' : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'}`}>
                      {unreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Desktop: vertical sidebar */}
          <div className="hidden lg:block card !p-3 space-y-4">
            {sectionGroups.map((group) => (
              group.keys.length > 0 && (
                <div key={group.label}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-secondary-400 dark:text-secondary-500 px-3 mb-1.5">
                    {group.label}
                  </p>
                  <div className="space-y-0.5">
                    {group.keys.map(([key, section]) => {
                      const Icon = section.icon;
                      const isActive = activeSection === key;
                      return (
                        <button
                          key={key}
                          onClick={() => handleSectionChange(key)}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            isActive
                              ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                              : 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'
                          }`}
                        >
                          <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary-600 dark:text-primary-400' : ''}`} />
                          <span className="flex-1 text-left">{section.label}</span>
                          {key === 'notifications' && unreadCount > 0 && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
                              {unreadCount}
                            </span>
                          )}
                          {isActive && <ChevronRight className="h-3.5 w-3.5 text-primary-400" />}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )
            ))}

            {/* Quick Links */}
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-secondary-400 dark:text-secondary-500 px-3 mb-1.5">
                Quick Links
              </p>
              <div className="space-y-0.5">
                <button onClick={() => navigate('/my-activity')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-all">
                  <Activity className="h-4 w-4" />
                  <span className="flex-1 text-left">My Activity</span>
                  <ExternalLink className="h-3 w-3 opacity-40" />
                </button>
                {isAdmin && (
                  <button onClick={() => navigate('/admin/chat-history')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-all">
                    <MessageSquare className="h-4 w-4" />
                    <span className="flex-1 text-left">Chat History</span>
                    <ExternalLink className="h-3 w-3 opacity-40" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <div className="card space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Personal Information</h2>
                <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">Update your name, email, and contact details.</p>
              </div>
              <form onSubmit={handleProfileUpdate} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                      <input type="text" className="input-field !pl-10" value={profileData.full_name} onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                      <input type="email" className="input-field !pl-10" value={profileData.email} onChange={(e) => setProfileData({ ...profileData, email: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Phone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                      <input type="tel" className="input-field !pl-10" value={profileData.phone} onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Department</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                      <input type="text" className="input-field !pl-10" value={profileData.department} onChange={(e) => setProfileData({ ...profileData, department: e.target.value })} disabled={!isAdmin} />
                    </div>
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Security Section (Password + 2FA combined) */}
          {activeSection === 'security' && (
            <div className="space-y-6">
              {/* Change Password */}
              <div className="card space-y-5">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 bg-primary-100 dark:bg-primary-900/40 rounded-xl flex items-center justify-center">
                    <Lock className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Change Password</h2>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">Update your account password.</p>
                  </div>
                </div>
                <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                  {!user?.oauth_provider && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Current Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                        <input type={showCurrentPassword ? 'text' : 'password'} className="input-field !pl-10 !pr-10" value={passwordData.current_password} onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })} required />
                        <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600">
                          {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                      <input type={showNewPassword ? 'text' : 'password'} className="input-field !pl-10 !pr-10" value={passwordData.new_password} onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })} required />
                      <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600">
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Confirm New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
                      <input type="password" className="input-field !pl-10" value={passwordData.confirm_password} onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })} required />
                    </div>
                  </div>
                  <div className="pt-1">
                    <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2">
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                      {loading ? 'Changing...' : 'Change Password'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Two-Factor Authentication */}
              <div className="card space-y-5">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 bg-primary-100 dark:bg-primary-900/40 rounded-xl flex items-center justify-center">
                    <Shield className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Two-Factor Authentication</h2>
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">Add an extra layer of security.</p>
                  </div>
                  {user?.two_factor_enabled ? (
                    <span className="px-2.5 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-semibold rounded-full flex items-center gap-1">
                      <Check className="h-3 w-3" /> Enabled
                    </span>
                  ) : (
                    <span className="px-2.5 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-xs font-semibold rounded-full">
                      Not Enabled
                    </span>
                  )}
                </div>

                {user?.two_factor_enabled ? (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button onClick={handleGenerateBackupCodes} disabled={loading} className="btn-secondary flex items-center gap-2 text-sm">
                      <Shield className="h-4 w-4" /> Generate Backup Codes
                    </button>
                    <button onClick={() => setShowDisable2FA(true)} className="btn-danger flex items-center gap-2 text-sm">
                      <Shield className="h-4 w-4" /> Disable 2FA
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-3">
                      Two-factor authentication requires a code from your authenticator app in addition to your password.
                    </p>
                    <button onClick={() => setShowSetup2FA(true)} className="btn-primary flex items-center gap-2 text-sm">
                      <Shield className="h-4 w-4" /> Setup 2FA
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Connected Apps Section */}
          {activeSection === 'integrations' && (
            <div className="space-y-6">
              <div className="card !p-0 overflow-hidden">
                <div className="p-5 border-b border-secondary-200 dark:border-secondary-700">
                  <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Connected Apps</h2>
                  <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">Manage your third-party app connections.</p>
                </div>

                {/* GitHub */}
                <div className="p-5 border-b border-secondary-100 dark:border-secondary-700/50">
                  {!githubConfigured && (
                    <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-sm text-yellow-800 dark:text-yellow-200">
                      GitHub OAuth is not configured. Contact your administrator to set it up.
                    </div>
                  )}
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 bg-secondary-100 dark:bg-secondary-700 rounded-xl flex items-center justify-center flex-shrink-0">
                      <GitHubIcon className="h-5 w-5 text-secondary-900 dark:text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">GitHub</h3>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                        {githubConnected ? `Connected as ${user?.github_username || 'your account'}` : 'Track commits and pull requests'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {githubConnected ? (
                        <>
                          <button onClick={() => navigate('/github-integration')} className="btn-secondary text-xs px-3 py-1.5">View</button>
                          <button onClick={handleDisconnectGitHub} className="btn-danger text-xs px-3 py-1.5">Disconnect</button>
                        </>
                      ) : (
                        <button onClick={handleConnectGitHub} disabled={loading} className="btn-primary text-xs px-3 py-1.5">
                          {loading ? 'Connecting...' : 'Connect'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Outlook Calendar */}
                <div className="p-5 border-b border-secondary-100 dark:border-secondary-700/50">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 bg-[#0078D4]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <OutlookIcon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Outlook Calendar</h3>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                        {microsoftCalendarConnected ? 'Connected to Outlook calendar' : 'Auto-track meetings from Outlook'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {microsoftCalendarConnected ? (
                        <>
                          <button onClick={() => navigate('/calendar')} className="btn-secondary text-xs px-3 py-1.5">View Calendar</button>
                          <button onClick={handleDisconnectMicrosoft} className="btn-danger text-xs px-3 py-1.5">Disconnect</button>
                        </>
                      ) : (
                        <button onClick={() => navigate('/calendar')} className="btn-primary text-xs px-3 py-1.5">Connect</button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Spotify */}
                <div className="p-5">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 bg-[#1DB954]/15 rounded-xl flex items-center justify-center flex-shrink-0">
                      <SpotifyIcon className="h-5 w-5 text-[#1DB954]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-secondary-900 dark:text-white">Spotify</h3>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                        Listen while you work and share what you&apos;re playing
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => navigate('/spotify')} className="btn-secondary text-xs px-3 py-1.5">
                        Manage
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Meeting Time Tracking (Outlook) */}
              {microsoftCalendarConnected && user?.role !== 'admin' && (
                <div className="card p-5 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <OutlookIcon className="h-5 w-5" />
                      <div>
                        <h4 className="text-sm font-medium text-secondary-900 dark:text-white">Meeting Time Tracking</h4>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400">Auto-create time entries from meetings</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {meetingTrackingLoading && <Loader2 className="h-3 w-3 animate-spin text-secondary-400" />}
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={meetingTimeTrackingEnabled} onChange={(e) => handleToggleMeetingTimeTracking(e.target.checked)} disabled={meetingTrackingLoading} className="sr-only" />
                        <div className={`w-11 h-6 rounded-full transition-all duration-200 ${meetingTimeTrackingEnabled ? 'bg-green-500' : 'bg-secondary-300 dark:bg-secondary-600'} ${meetingTrackingLoading ? 'opacity-50' : ''}`}>
                          <div className={`w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-200 mt-0.5 ${meetingTimeTrackingEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                        </div>
                      </label>
                    </div>
                  </div>
                  {meetingTimeTrackingEnabled && (
                    <div className="flex items-center justify-between pl-8">
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">Manually process recent meetings now</p>
                      <button onClick={handleTriggerMeetingTracking} disabled={triggerMeetingTrackingLoading} className="btn-secondary text-xs px-3 py-1.5 flex items-center gap-1.5">
                        {triggerMeetingTrackingLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                        {triggerMeetingTrackingLoading ? 'Processing...' : 'Sync Now'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Appearance Section */}
          {activeSection === 'appearance' && (
            <div className="card space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Appearance</h2>
                <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">Choose how the app looks. System follows your device setting.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { value: 'light', label: 'Light', icon: Sun, desc: 'Clean and bright' },
                  { value: 'dark', label: 'Dark', icon: Moon, desc: 'Easy on the eyes' },
                  { value: 'system', label: 'System', icon: Monitor, desc: 'Follows your OS' },
                ].map((opt) => {
                  const Icon = opt.icon;
                  const isSelected = theme === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setTheme(opt.value)}
                      className={`relative flex flex-col items-center gap-2 p-5 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-sm'
                          : 'border-secondary-200 dark:border-secondary-700 hover:border-secondary-300 dark:hover:border-secondary-600'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-2 right-2 h-5 w-5 bg-primary-600 rounded-full flex items-center justify-center">
                          <Check className="h-3 w-3 text-white" />
                        </div>
                      )}
                      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${isSelected ? 'bg-primary-100 dark:bg-primary-900/40' : 'bg-secondary-100 dark:bg-secondary-700'}`}>
                        <Icon className={`h-5 w-5 ${isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-secondary-500 dark:text-secondary-400'}`} />
                      </div>
                      <span className={`text-sm font-semibold ${isSelected ? 'text-primary-700 dark:text-primary-300' : 'text-secondary-700 dark:text-secondary-300'}`}>{opt.label}</span>
                      <span className="text-xs text-secondary-500 dark:text-secondary-400">{opt.desc}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Notifications Section */}
          {activeSection === 'notifications' && (
            <div className="card space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Notifications</h2>
                  <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">
                    {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                  </p>
                </div>
              </div>
              {notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="h-10 w-10 text-secondary-300 dark:text-secondary-600 mx-auto mb-3" />
                  <p className="text-secondary-500 dark:text-secondary-400 text-sm">No notifications yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {notifications.slice(0, 10).map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-3.5 rounded-xl border cursor-pointer transition-all hover:shadow-sm ${
                        notification.is_read
                          ? 'bg-secondary-50/50 dark:bg-secondary-800/50 border-secondary-200 dark:border-secondary-700'
                          : 'bg-primary-50/50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'
                      }`}
                      onClick={() => !notification.is_read && markNotificationRead(notification.id)}
                    >
                      <div className="flex items-start gap-3">
                        {!notification.is_read && (
                          <div className="h-2 w-2 bg-primary-600 rounded-full mt-1.5 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm text-secondary-900 dark:text-white">{notification.title}</h4>
                          <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-0.5 line-clamp-2">{notification.message}</p>
                          <p className="text-[11px] text-secondary-400 dark:text-secondary-500 mt-1.5">{toUTCPlus5(notification.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* System Configuration (Admin Only) */}
          {activeSection === 'system' && isAdmin && (
            <div className="card space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">System Configuration</h2>
                <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-0.5">Manage global settings for your organization.</p>
              </div>
              {loadingConfig ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : (
                <form
                  data-testid="system-config-form"
                  onSubmit={async e => {
                    e.preventDefault();
                    setSavingConfig(true);
                    try {
                      const { data: savedSettings } = await axios.put('/api/system-settings/', {
                        idle_threshold_minutes: systemConfig.idle_threshold_minutes,
                        enable_manual_logging: systemConfig.enable_manual_logging,
                        ai_prompt_template: systemConfig.ai_prompt_template,
                        ai_model: systemConfig.ai_model,
                        office_name: systemConfig.office_name,
                        office_latitude: systemConfig.office_latitude,
                        office_longitude: systemConfig.office_longitude,
                        office_radius_km: systemConfig.office_radius_km,
                        daily_hours_cap: systemConfig.daily_hours_cap,
                        weekly_target_hours: systemConfig.weekly_target_hours,
                        daily_target_hours: systemConfig.daily_target_hours,
                        flagged_entry_threshold_hours: systemConfig.flagged_entry_threshold_hours,
                        timer_auto_stop_hours: systemConfig.timer_auto_stop_hours,
                        min_description_length: systemConfig.min_description_length,
                        entry_edit_window_hours: systemConfig.entry_edit_window_hours,
                        under_target_threshold_percent: systemConfig.under_target_threshold_percent,
                        annual_leave_limit: systemConfig.annual_leave_limit,
                        casual_leave_limit: systemConfig.casual_leave_limit,
                        medical_leave_limit: systemConfig.medical_leave_limit,
                        same_day_leave_cutoff_hour: systemConfig.same_day_leave_cutoff_hour,
                        working_days: systemConfig.working_days,
                        company_holidays: systemConfig.company_holidays || [],
                        work_week_start_day: systemConfig.work_week_start_day,
                        business_hours_start: systemConfig.business_hours_start,
                        business_hours_end: systemConfig.business_hours_end,
                        app_timezone_offset: systemConfig.app_timezone_offset,
                        min_meeting_duration_minutes: systemConfig.min_meeting_duration_minutes,
                        meeting_end_buffer_minutes: systemConfig.meeting_end_buffer_minutes,
                        escalation_overdue_days: systemConfig.escalation_overdue_days,
                        max_subtask_hours: systemConfig.max_subtask_hours,
                        max_file_upload_mb: systemConfig.max_file_upload_mb,
                        currency_code: systemConfig.currency_code,
                        weekly_hours_enforcement_enabled: systemConfig.weekly_hours_enforcement_enabled,
                        weekly_hours_dry_run_enabled: systemConfig.weekly_hours_dry_run_enabled,
                        weekly_hours_test_recipient_emails: systemConfig.weekly_hours_test_recipient_emails || [],
                        member_pace_factor: systemConfig.member_pace_factor,
                        weekly_report_recipient_user_id: systemConfig.weekly_report_recipient_user_id ?? '',
                        notify_team_lead_on_final_reminder: systemConfig.notify_team_lead_on_final_reminder,
                        member_reminder_days: systemConfig.member_reminder_days,
                        member_reminder_hour: systemConfig.member_reminder_hour,
                        member_reminder_minute: systemConfig.member_reminder_minute,
                        member_reminder_levels: systemConfig.member_reminder_levels,
                        member_reminder_departments: systemConfig.member_reminder_departments || [],
                        member_reminder_excluded_user_ids: systemConfig.member_reminder_excluded_user_ids || [],
                        hr_report_departments: systemConfig.hr_report_departments || [],
                        hr_report_excluded_user_ids: systemConfig.hr_report_excluded_user_ids || [],
                        lead_summary_day: systemConfig.lead_summary_day,
                        lead_summary_hour: systemConfig.lead_summary_hour,
                        lead_summary_minute: systemConfig.lead_summary_minute,
                        hr_report_day: systemConfig.hr_report_day,
                        hr_report_hour: systemConfig.hr_report_hour,
                        hr_report_minute: systemConfig.hr_report_minute,
                      });
                      queryClient.setQueryData(
                        ['systemSettings'],
                        mergeSystemSettings(savedSettings)
                      );
                      await queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
                      toast.success('System settings updated');
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to update settings');
                    } finally {
                      setSavingConfig(false);
                    }
                  }}
                  className="space-y-6"
                >
                  {/* Time Tracking */}
                  <div className="space-y-4" data-testid="system-config-section-time-tracking">
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wide">Time Tracking</h3>
                    <div className="max-w-xs">
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Idle Threshold (minutes)</label>
                      <input data-testid="idle-threshold-input" type="number" min={1} max={60} className="input-field" value={systemConfig.idle_threshold_minutes} onChange={e => setConfigField('idle_threshold_minutes', Number(e.target.value))} />
                      <p className="text-xs text-secondary-500 mt-1">Minutes of inactivity before idle.</p>
                    </div>
                    <div className="flex items-center justify-between gap-4 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50/60 dark:bg-secondary-800/40 px-4 py-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-secondary-900 dark:text-white">Manual Logging</p>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">Allow members to log time manually.</p>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={systemConfig.enable_manual_logging}
                        data-testid="manual-logging-toggle"
                        onClick={() =>
                          setConfigField('enable_manual_logging', !systemConfig.enable_manual_logging)
                        }
                        className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-secondary-900 ${
                          systemConfig.enable_manual_logging
                            ? 'bg-primary-600'
                            : 'bg-secondary-300 dark:bg-secondary-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            systemConfig.enable_manual_logging ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Time Tracking Rules */}
                  <div className="space-y-4 pt-4 border-t border-secondary-200 dark:border-secondary-700" data-testid="system-config-section-time-tracking-rules">
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wide">Time Tracking Rules</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Daily Hours Cap</label>
                        <input data-testid="daily-hours-cap-input" type="number" min={0.1} step="any" className="input-field" value={systemConfig.daily_hours_cap} onChange={e => setConfigField('daily_hours_cap', parseFloat(e.target.value))} />
                        <p className="text-xs text-secondary-500 mt-1">Max hours a member can log per day.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Weekly Target Hours</label>
                        <input data-testid="weekly-target-hours-input" type="number" min={0.1} step="any" className="input-field" value={systemConfig.weekly_target_hours} onChange={e => setConfigField('weekly_target_hours', parseFloat(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Daily Target Hours</label>
                        <input data-testid="daily-target-hours-input" type="number" min={0.1} step="any" className="input-field" value={systemConfig.daily_target_hours} onChange={e => setConfigField('daily_target_hours', parseFloat(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Flagged Entry Threshold (hours)</label>
                        <input data-testid="flagged-entry-threshold-input" type="number" min={0.1} step="any" className="input-field" value={systemConfig.flagged_entry_threshold_hours} onChange={e => setConfigField('flagged_entry_threshold_hours', parseFloat(e.target.value))} />
                        <p className="text-xs text-secondary-500 mt-1">Entries longer than this are flagged.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Timer Auto-Stop (hours)</label>
                        <input data-testid="timer-auto-stop-input" type="number" min={0.1} step="any" className="input-field" value={systemConfig.timer_auto_stop_hours} onChange={e => setConfigField('timer_auto_stop_hours', parseFloat(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Min Description Length</label>
                        <input data-testid="min-description-length-input" type="number" min={1} max={5000} className="input-field" value={systemConfig.min_description_length} onChange={e => setConfigField('min_description_length', Number(e.target.value))} />
                      </div>
                    </div>
                  </div>

                  {/* Locking & Edit Rules */}
                  <div className="space-y-4 pt-4 border-t border-secondary-200 dark:border-secondary-700" data-testid="system-config-section-locking">
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wide">Locking & Edit Rules</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Entry Edit Window (hours)</label>
                        <input data-testid="entry-edit-window-input" type="number" min={1} className="input-field" value={systemConfig.entry_edit_window_hours} onChange={e => setConfigField('entry_edit_window_hours', Number(e.target.value))} />
                        <p className="text-xs text-secondary-500 mt-1">Hours after task completion that entries can be edited.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Under-Target Threshold (%)</label>
                        <input data-testid="under-target-threshold-input" type="number" min={1} max={100} className="input-field" value={systemConfig.under_target_threshold_percent} onChange={e => setConfigField('under_target_threshold_percent', parseFloat(e.target.value))} />
                        <p className="text-xs text-secondary-500 mt-1">% of weekly target below which status is critical.</p>
                      </div>
                    </div>
                  </div>

                  {/* Leave Policy */}
                  <div className="space-y-4 pt-4 border-t border-secondary-200 dark:border-secondary-700" data-testid="system-config-section-leave-policy">
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wide">Leave Policy</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Annual Leave Limit (days)</label>
                        <input data-testid="annual-leave-limit-input" type="number" min={0} className="input-field" value={systemConfig.annual_leave_limit} onChange={e => setConfigField('annual_leave_limit', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Casual Leave Limit (days)</label>
                        <input data-testid="casual-leave-limit-input" type="number" min={0} className="input-field" value={systemConfig.casual_leave_limit} onChange={e => setConfigField('casual_leave_limit', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Medical Leave Limit (days)</label>
                        <input data-testid="medical-leave-limit-input" type="number" min={0} className="input-field" value={systemConfig.medical_leave_limit} onChange={e => setConfigField('medical_leave_limit', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Same-Day Leave Cutoff (hour)</label>
                        <input data-testid="same-day-leave-cutoff-input" type="number" min={0} max={23} className="input-field" value={systemConfig.same_day_leave_cutoff_hour} onChange={e => setConfigField('same_day_leave_cutoff_hour', Number(e.target.value))} />
                        <p className="text-xs text-secondary-500 mt-1">0–23 in app timezone (UTC+5). Members cannot mark leave for Today at/after this hour (e.g. 10 = 10:00 AM).</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">Working Days</label>
                      <div className="flex flex-wrap gap-2" data-testid="working-days-group">
                        {DAY_LABELS.map(({ value, label }) => {
                          const active = (systemConfig.working_days || []).includes(value);
                          return (
                            <button
                              key={value}
                              type="button"
                              data-testid={`working-day-${value}`}
                              onClick={() => toggleWorkingDay(value)}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                active
                                  ? 'bg-primary-600 text-white border-primary-600'
                                  : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 border-secondary-300 dark:border-secondary-600'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div data-testid="company-holidays-section">
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                        Company Holidays
                      </label>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-3">
                        Holidays on working days lower the weekly hour target the same way leave does
                        (e.g. 1 holiday → 32h instead of 40h).
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2 mb-3">
                        <input
                          data-testid="holiday-date-input"
                          type="date"
                          className="input-field sm:w-44"
                          value={holidayDraft.date}
                          onChange={(e) =>
                            setHolidayDraft((prev) => ({ ...prev, date: e.target.value }))
                          }
                        />
                        <input
                          data-testid="holiday-name-input"
                          type="text"
                          className="input-field flex-1"
                          placeholder="Holiday name (optional)"
                          value={holidayDraft.name}
                          onChange={(e) =>
                            setHolidayDraft((prev) => ({ ...prev, name: e.target.value }))
                          }
                          maxLength={120}
                        />
                        <button
                          type="button"
                          data-testid="holiday-add-btn"
                          onClick={addCompanyHoliday}
                          className="btn-secondary flex items-center justify-center gap-1.5 whitespace-nowrap"
                        >
                          <Plus className="h-4 w-4" />
                          Add
                        </button>
                      </div>
                      {(systemConfig.company_holidays || []).length === 0 ? (
                        <p className="text-sm text-secondary-500 dark:text-secondary-400">
                          No holidays configured.
                        </p>
                      ) : (
                        <ul className="divide-y divide-secondary-100 dark:divide-secondary-700 border border-secondary-200 dark:border-secondary-700 rounded-lg overflow-hidden">
                          {(systemConfig.company_holidays || []).map((h) => (
                            <li
                              key={h.date}
                              className="flex items-center justify-between gap-3 px-3 py-2 bg-white dark:bg-secondary-800"
                              data-testid={`holiday-row-${h.date}`}
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-secondary-900 dark:text-white">
                                  {h.date}
                                  {h.name ? (
                                    <span className="font-normal text-secondary-600 dark:text-secondary-300">
                                      {' '}
                                      — {h.name}
                                    </span>
                                  ) : null}
                                </p>
                              </div>
                              <button
                                type="button"
                                data-testid={`holiday-remove-${h.date}`}
                                onClick={() => removeCompanyHoliday(h.date)}
                                className="p-1.5 rounded-lg text-secondary-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                aria-label={`Remove holiday ${h.date}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  {/* Work Schedule */}
                  <div className="space-y-4 pt-4 border-t border-secondary-200 dark:border-secondary-700" data-testid="system-config-section-work-schedule">
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wide">Work Schedule</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Work Week Start Day</label>
                        <select data-testid="work-week-start-input" className="input-field" value={systemConfig.work_week_start_day} onChange={e => setConfigField('work_week_start_day', Number(e.target.value))}>
                          {DAY_LABELS.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Timezone Offset (UTC)</label>
                        <input data-testid="timezone-offset-input" type="number" min={-12} max={14} className="input-field" value={systemConfig.app_timezone_offset} onChange={e => setConfigField('app_timezone_offset', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Business Hours Start</label>
                        <input data-testid="business-hours-start-input" type="number" min={0} max={23} className="input-field" value={systemConfig.business_hours_start} onChange={e => setConfigField('business_hours_start', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Business Hours End</label>
                        <input data-testid="business-hours-end-input" type="number" min={0} max={24} className="input-field" value={systemConfig.business_hours_end} onChange={e => setConfigField('business_hours_end', Number(e.target.value))} />
                      </div>
                    </div>
                  </div>

                  {/* Automation */}
                  <div className="space-y-4 pt-4 border-t border-secondary-200 dark:border-secondary-700" data-testid="system-config-section-automation">
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wide">Meetings & Automation</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Min Meeting Duration (minutes)</label>
                        <input data-testid="min-meeting-duration-input" type="number" min={1} className="input-field" value={systemConfig.min_meeting_duration_minutes} onChange={e => setConfigField('min_meeting_duration_minutes', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Meeting End Buffer (minutes)</label>
                        <input data-testid="meeting-end-buffer-input" type="number" min={0} className="input-field" value={systemConfig.meeting_end_buffer_minutes} onChange={e => setConfigField('meeting_end_buffer_minutes', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Escalation Overdue (days)</label>
                        <input data-testid="escalation-overdue-input" type="number" min={1} className="input-field" value={systemConfig.escalation_overdue_days} onChange={e => setConfigField('escalation_overdue_days', Number(e.target.value))} />
                      </div>
                    </div>
                  </div>

                  {/* Task Limits */}
                  <div className="space-y-4 pt-4 border-t border-secondary-200 dark:border-secondary-700" data-testid="system-config-section-task-limits">
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wide">Task & Scope Limits</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Max Subtask Hours</label>
                        <input data-testid="max-subtask-hours-input" type="number" min={1} className="input-field" value={systemConfig.max_subtask_hours} onChange={e => setConfigField('max_subtask_hours', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Max File Upload (MB)</label>
                        <input data-testid="max-file-upload-input" type="number" min={1} max={500} className="input-field" value={systemConfig.max_file_upload_mb} onChange={e => setConfigField('max_file_upload_mb', Number(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Currency Code</label>
                        <input data-testid="currency-code-input" type="text" maxLength={3} className="input-field uppercase" value={systemConfig.currency_code} onChange={e => setConfigField('currency_code', e.target.value.toUpperCase())} />
                      </div>
                    </div>
                  </div>

                  {/* AI Configuration */}
                  <div className="space-y-4 pt-4 border-t border-secondary-200 dark:border-secondary-700" data-testid="system-config-section-ai">
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wide">AI Configuration</h3>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Model for Task Estimation</label>
                      <select data-testid="ai-model-select" className="input-field" value={systemConfig.ai_model} onChange={e => setConfigField('ai_model', e.target.value)}>
                        <optgroup label="OpenAI">
                          <option value="gpt-4o-mini">gpt-4o-mini</option>
                          <option value="gpt-4o">gpt-4o</option>
                          <option value="gpt-4-turbo">gpt-4-turbo</option>
                          <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                        </optgroup>
                        <optgroup label="Gemini">
                          <option value="gemini-2.5-flash">gemini-2.5-flash</option>
                          <option value="gemini-2.0-flash">gemini-2.0-flash</option>
                          <option value="gemini-2.0-flash-lite">gemini-2.0-flash-lite</option>
                          <option value="gemini-1.5-flash">gemini-1.5-flash</option>
                          <option value="gemini-1.5-pro">gemini-1.5-pro</option>
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Prompt Template</label>
                      <textarea data-testid="ai-prompt-template-input" className="input-field font-mono text-sm" rows={5} value={systemConfig.ai_prompt_template} onChange={e => setConfigField('ai_prompt_template', e.target.value)} placeholder="Enter the prompt template..." />
                      <p className="text-xs text-secondary-500 mt-1">Use <code className="bg-secondary-100 dark:bg-secondary-700 px-1 rounded">{'{title}'}</code> and <code className="bg-secondary-100 dark:bg-secondary-700 px-1 rounded">{'{description}'}</code> as placeholders.</p>
                    </div>
                  </div>

                  {/* Office Location */}
                  <div className="space-y-4 pt-4 border-t border-secondary-200 dark:border-secondary-700" data-testid="system-config-section-office">
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wide flex items-center gap-2">
                      <Building className="h-4 w-4" /> Office Location
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Office Name</label>
                        <input data-testid="office-name-input" type="text" className="input-field" value={systemConfig.office_name} onChange={e => setConfigField('office_name', e.target.value)} placeholder="e.g., Main Office" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Radius (meters)</label>
                        <input data-testid="office-radius-input" type="number" className="input-field" value={Math.round((systemConfig.office_radius_km || 0) * 1000)} onChange={e => setConfigField('office_radius_km', parseFloat(e.target.value) / 1000)} min="100" max="10000" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Latitude</label>
                        <input data-testid="office-latitude-input" type="number" step="any" className="input-field" value={systemConfig.office_latitude} onChange={e => setConfigField('office_latitude', parseFloat(e.target.value))} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Longitude</label>
                        <input data-testid="office-longitude-input" type="number" step="any" className="input-field" value={systemConfig.office_longitude} onChange={e => setConfigField('office_longitude', parseFloat(e.target.value))} />
                      </div>
                    </div>
                  </div>

                  {/* Weekly Hours Enforcement */}
                  <div className="space-y-6 pt-4 border-t border-secondary-200 dark:border-secondary-700" data-testid="system-config-section-weekly-hours">
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300 uppercase tracking-wide">Weekly Hours Enforcement</h3>

                    {/* Sub-card A: General */}
                    <div className="space-y-4 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4" data-testid="weekly-hours-general">
                      <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide">General</p>
                      <div className="flex items-center justify-between gap-4 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50/60 dark:bg-secondary-800/40 px-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-secondary-900 dark:text-white">Enable weekly-hours reminders</p>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">Master toggle for member, lead, and HR jobs.</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!systemConfig.weekly_hours_enforcement_enabled}
                          data-testid="weekly-hours-enforcement-toggle"
                          onClick={() => setConfigField('weekly_hours_enforcement_enabled', !systemConfig.weekly_hours_enforcement_enabled)}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-secondary-900 ${
                            systemConfig.weekly_hours_enforcement_enabled ? 'bg-primary-600' : 'bg-secondary-300 dark:bg-secondary-600'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            systemConfig.weekly_hours_enforcement_enabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>

                      <div className="flex items-center justify-between gap-4 rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50/70 dark:bg-amber-900/20 px-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-secondary-900 dark:text-white">Dry run (test recipients only)</p>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">
                            When on, emails and notifications only go to the allowlist below — safe for production Trigger now.
                          </p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!systemConfig.weekly_hours_dry_run_enabled}
                          data-testid="weekly-hours-dry-run-toggle"
                          onClick={() => setConfigField('weekly_hours_dry_run_enabled', !systemConfig.weekly_hours_dry_run_enabled)}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-secondary-900 ${
                            systemConfig.weekly_hours_dry_run_enabled ? 'bg-amber-500' : 'bg-secondary-300 dark:bg-secondary-600'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            systemConfig.weekly_hours_dry_run_enabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>

                      {systemConfig.weekly_hours_dry_run_enabled && (
                        <div data-testid="weekly-hours-test-recipients">
                          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">
                            Test recipient emails
                          </label>
                          <textarea
                            data-testid="weekly-hours-test-recipients-input"
                            className="input-field min-h-[88px] font-mono text-sm"
                            placeholder={'one@example.com\ntwo@example.com'}
                            value={(systemConfig.weekly_hours_test_recipient_emails || []).join('\n')}
                            onChange={(e) => {
                              const emails = e.target.value
                                .split(/[\n,;]+/)
                                .map((s) => s.trim())
                                .filter(Boolean);
                              setConfigField('weekly_hours_test_recipient_emails', emails);
                            }}
                          />
                          <p className="text-xs text-secondary-500 mt-1">
                            One email per line (or comma-separated). Subjects are prefixed with [DRY RUN].
                          </p>
                        </div>
                      )}

                      <div className="relative">
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Weekly report recipient (HR)</label>
                        {(() => {
                          const selected = allUsers.find(u => u.id === systemConfig.weekly_report_recipient_user_id);
                          const filtered = allUsers.filter(u => {
                            const q = hrSearch.trim().toLowerCase();
                            if (!q) return true;
                            const hay = `${u.full_name || ''} ${u.username || ''} ${u.email || ''} ${u.department || ''}`.toLowerCase();
                            return hay.includes(q);
                          });
                          return (
                            <>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  data-testid="hr-recipient-picker"
                                  onClick={() => setHrPickerOpen(o => !o)}
                                  className="input-field flex-1 text-left"
                                >
                                  {selected
                                    ? `${selected.full_name || selected.username} (${selected.role})`
                                    : 'Select a user…'}
                                </button>
                                {systemConfig.weekly_report_recipient_user_id && (
                                  <button
                                    type="button"
                                    data-testid="hr-recipient-clear"
                                    className="btn-secondary px-3"
                                    onClick={() => setConfigField('weekly_report_recipient_user_id', '')}
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                              {hrPickerOpen && (
                                <div className="absolute z-20 mt-1 w-full rounded-lg border border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 shadow-lg max-h-56 overflow-auto">
                                  <div className="p-2 sticky top-0 bg-white dark:bg-secondary-800">
                                    <input
                                      data-testid="hr-recipient-search"
                                      type="text"
                                      className="input-field"
                                      placeholder="Search users…"
                                      value={hrSearch}
                                      onChange={e => setHrSearch(e.target.value)}
                                      autoFocus
                                    />
                                  </div>
                                  {filtered.map(u => (
                                    <button
                                      key={u.id}
                                      type="button"
                                      data-testid={`hr-recipient-option-${u.id}`}
                                      className="w-full text-left px-3 py-2 text-sm hover:bg-secondary-50 dark:hover:bg-secondary-700"
                                      onClick={() => {
                                        setConfigField('weekly_report_recipient_user_id', u.id);
                                        setHrPickerOpen(false);
                                        setHrSearch('');
                                      }}
                                    >
                                      <span className="font-medium text-secondary-900 dark:text-white">{u.full_name || u.username}</span>
                                      <span className="text-secondary-500 ml-2 text-xs">{u.role}{u.department ? ` · ${u.department}` : ''}</span>
                                    </button>
                                  ))}
                                  {filtered.length === 0 && (
                                    <p className="px-3 py-2 text-sm text-secondary-500">No users found</p>
                                  )}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>

                      <div className="max-w-xs">
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Member pace factor (%)</label>
                        <input
                          data-testid="member-pace-factor-input"
                          type="number"
                          min={0}
                          max={100}
                          step={5}
                          className="input-field"
                          value={Math.round((systemConfig.member_pace_factor ?? 0.75) * 100)}
                          onChange={e => setConfigField('member_pace_factor', Math.min(100, Math.max(0, Number(e.target.value))) / 100)}
                        />
                        <p className="text-xs text-secondary-500 mt-1">How close to the daily target a member must track before we skip the reminder.</p>
                      </div>

                      <div className="flex items-center justify-between gap-4 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50/60 dark:bg-secondary-800/40 px-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-secondary-900 dark:text-white">CC team lead on final reminder</p>
                          <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">Also notify the member&apos;s team lead on the final escalation.</p>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!systemConfig.notify_team_lead_on_final_reminder}
                          data-testid="notify-team-lead-final-toggle"
                          onClick={() => setConfigField('notify_team_lead_on_final_reminder', !systemConfig.notify_team_lead_on_final_reminder)}
                          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-secondary-900 ${
                            systemConfig.notify_team_lead_on_final_reminder ? 'bg-primary-600' : 'bg-secondary-300 dark:bg-secondary-600'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            systemConfig.notify_team_lead_on_final_reminder ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                    </div>

                    {/* Sub-card B: Member reminders */}
                    <div className="space-y-4 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4" data-testid="weekly-hours-member-schedule">
                      <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide">Member reminders schedule</p>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">Reminder days</label>
                        <div className="flex flex-wrap gap-2" data-testid="member-reminder-days-picker">
                          {DAY_LABELS.map(({ value, label }) => {
                            const active = (systemConfig.member_reminder_days || []).includes(value);
                            return (
                              <button
                                key={value}
                                type="button"
                                data-testid={`member-reminder-day-${value}`}
                                onClick={() => toggleReminderDay(value)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                  active
                                    ? 'bg-primary-600 text-white border-primary-600'
                                    : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 border-secondary-300 dark:border-secondary-600'
                                }`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div data-testid="member-reminder-departments">
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                          Departments
                        </label>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
                          Leave none selected to email all departments. Select one or more to limit reminders
                          (from Resource Utilization departments).
                        </p>
                        {availableDepartments.length === 0 ? (
                          <p className="text-sm text-secondary-500 dark:text-secondary-400">
                            No departments found. Set departments on the Resource Utilization page first.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-2" data-testid="member-reminder-departments-picker">
                            {availableDepartments.map((dept) => {
                              const active = (systemConfig.member_reminder_departments || []).includes(dept);
                              return (
                                <button
                                  key={dept}
                                  type="button"
                                  data-testid={`member-reminder-dept-${dept}`}
                                  onClick={() => toggleReminderDepartment(dept)}
                                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                    active
                                      ? 'bg-primary-600 text-white border-primary-600'
                                      : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 border-secondary-300 dark:border-secondary-600'
                                  }`}
                                >
                                  {dept}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {(systemConfig.member_reminder_departments || []).length > 0 && (
                          <button
                            type="button"
                            data-testid="member-reminder-departments-clear"
                            className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                            onClick={() => setSystemConfig((prev) => ({
                              ...prev,
                              member_reminder_departments: [],
                              member_reminder_excluded_user_ids: [],
                            }))}
                          >
                            Clear selection (all departments)
                          </button>
                        )}
                        {(systemConfig.member_reminder_departments || []).length > 0 && (
                          <div
                            className="mt-3 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50/60 dark:bg-secondary-900/30 p-3"
                            data-testid="member-reminder-users-picker"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                              <p className="text-xs font-medium text-secondary-700 dark:text-secondary-300">
                                People in selected departments
                              </p>
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                                  data-testid="member-reminder-users-select-all"
                                  onClick={() => setAudienceUsersSelected(
                                    'member_reminder_excluded_user_ids',
                                    memberReminderRecipientPreview.pool,
                                    true
                                  )}
                                >
                                  Select all
                                </button>
                                <button
                                  type="button"
                                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                                  data-testid="member-reminder-users-clear-all"
                                  onClick={() => setAudienceUsersSelected(
                                    'member_reminder_excluded_user_ids',
                                    memberReminderRecipientPreview.pool,
                                    false
                                  )}
                                >
                                  Unselect all
                                </button>
                              </div>
                            </div>
                            {memberReminderRecipientPreview.pool.length === 0 ? (
                              <p className="text-xs text-secondary-500">No eligible members/leads in these departments.</p>
                            ) : (
                              <ul className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                                {memberReminderRecipientPreview.pool
                                  .slice()
                                  .sort((a, b) =>
                                    String(a.full_name || a.username || a.email || '').localeCompare(
                                      String(b.full_name || b.username || b.email || '')
                                    )
                                  )
                                  .map((u) => {
                                    const checked = !(systemConfig.member_reminder_excluded_user_ids || []).includes(u.id);
                                    return (
                                      <li key={u.id}>
                                        <label className="flex items-start gap-2 text-sm text-secondary-800 dark:text-secondary-200 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            className="mt-0.5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                                            data-testid={`member-reminder-user-${u.id}`}
                                            checked={checked}
                                            onChange={() => toggleAudienceUser('member_reminder_excluded_user_ids', u.id)}
                                          />
                                          <span className="min-w-0">
                                            <span className="font-medium">{u.full_name || u.username || u.email}</span>
                                            <span className="block text-xs text-secondary-500 truncate">
                                              {u.department}{u.email ? ` · ${u.email}` : ''}
                                            </span>
                                          </span>
                                        </label>
                                      </li>
                                    );
                                  })}
                              </ul>
                            )}
                          </div>
                        )}
                        <p
                          className="mt-2 text-xs text-secondary-600 dark:text-secondary-400"
                          data-testid="member-reminder-recipient-count"
                        >
                          {memberReminderRecipientPreview.filtered ? (
                            <>
                              <span className="font-medium text-secondary-800 dark:text-secondary-200">
                                {memberReminderRecipientPreview.count}
                              </span>
                              {' '}
                              {memberReminderRecipientPreview.count === 1 ? 'person' : 'people'} in selected
                              {' '}department{ (systemConfig.member_reminder_departments || []).length === 1 ? '' : 's'}
                              {' '}may receive reminders
                              {' '}
                              <span className="text-secondary-500">
                                (of {memberReminderRecipientPreview.totalEligible} eligible members/leads;
                                only if behind pace and not on leave)
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="font-medium text-secondary-800 dark:text-secondary-200">
                                {memberReminderRecipientPreview.count}
                              </span>
                              {' '}
                              eligible members/leads across all departments may receive reminders
                              {' '}
                              <span className="text-secondary-500">
                                (only if behind pace and not on leave)
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4 max-w-sm">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Hour</label>
                          <select
                            data-testid="member-reminder-hour"
                            className="input-field"
                            value={systemConfig.member_reminder_hour}
                            onChange={e => setConfigField('member_reminder_hour', Number(e.target.value))}
                          >
                            {HOUR_OPTIONS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Minute</label>
                          <select
                            data-testid="member-reminder-minute"
                            className="input-field"
                            value={systemConfig.member_reminder_minute}
                            onChange={e => setConfigField('member_reminder_minute', Number(e.target.value))}
                          >
                            {MINUTE_OPTIONS.map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
                          </select>
                        </div>
                      </div>
                      <p className="text-xs text-secondary-500" data-testid="member-reminder-next-run">
                        Next run at: {formatNextRunPreview({
                          days: systemConfig.member_reminder_days,
                          hour: systemConfig.member_reminder_hour,
                          minute: systemConfig.member_reminder_minute,
                        })}
                      </p>
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">Escalation levels (order)</label>
                        <div className="flex flex-wrap gap-2" data-testid="member-reminder-levels">
                          {(systemConfig.member_reminder_levels || REMINDER_LEVEL_OPTIONS).map((level, idx) => (
                            <div key={`${level}-${idx}`} className="flex items-center gap-1 rounded-lg border border-secondary-300 dark:border-secondary-600 px-2 py-1">
                              <span className="text-sm capitalize">{level}</span>
                              <button type="button" data-testid={`level-up-${idx}`} className="text-xs text-secondary-500 px-1" onClick={() => moveReminderLevel(idx, -1)} disabled={idx === 0}>↑</button>
                              <button type="button" data-testid={`level-down-${idx}`} className="text-xs text-secondary-500 px-1" onClick={() => moveReminderLevel(idx, 1)} disabled={idx === (systemConfig.member_reminder_levels || []).length - 1}>↓</button>
                            </div>
                          ))}
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="trigger-weekly-pace-reminders"
                        disabled={!!triggeringJob}
                        onClick={() => triggerWeeklyJob('trigger-weekly-pace-reminders', 'Member pace reminders')}
                        className="btn-secondary text-sm"
                      >
                        {triggeringJob === 'trigger-weekly-pace-reminders' ? 'Triggering…' : 'Trigger now'}
                      </button>
                    </div>

                    {/* Sub-card C: Summaries */}
                    <div className="space-y-4 rounded-lg border border-secondary-200 dark:border-secondary-700 p-4" data-testid="weekly-hours-summaries-schedule">
                      <p className="text-xs font-semibold text-secondary-500 uppercase tracking-wide">Weekly summaries schedule</p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Team lead summary day</label>
                          <select data-testid="lead-summary-day" className="input-field" value={systemConfig.lead_summary_day} onChange={e => setConfigField('lead_summary_day', Number(e.target.value))}>
                            {DAY_LABELS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Hour</label>
                          <select data-testid="lead-summary-hour" className="input-field" value={systemConfig.lead_summary_hour} onChange={e => setConfigField('lead_summary_hour', Number(e.target.value))}>
                            {HOUR_OPTIONS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Minute</label>
                          <select data-testid="lead-summary-minute" className="input-field" value={systemConfig.lead_summary_minute} onChange={e => setConfigField('lead_summary_minute', Number(e.target.value))}>
                            {MINUTE_OPTIONS.map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
                          </select>
                        </div>
                      </div>
                      <p className="text-xs text-secondary-500" data-testid="lead-summary-next-run">
                        Next lead summary: {formatNextRunPreview({
                          days: [systemConfig.lead_summary_day],
                          hour: systemConfig.lead_summary_hour,
                          minute: systemConfig.lead_summary_minute,
                        })}
                      </p>
                      <button
                        type="button"
                        data-testid="trigger-team-lead-summary"
                        disabled={!!triggeringJob}
                        onClick={() => triggerWeeklyJob('trigger-team-lead-summary', 'Team lead summary')}
                        className="btn-secondary text-sm"
                      >
                        {triggeringJob === 'trigger-team-lead-summary' ? 'Triggering…' : 'Trigger lead summary now'}
                      </button>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">HR report day</label>
                          <select data-testid="hr-report-day" className="input-field" value={systemConfig.hr_report_day} onChange={e => setConfigField('hr_report_day', Number(e.target.value))}>
                            {DAY_LABELS.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Hour</label>
                          <select data-testid="hr-report-hour" className="input-field" value={systemConfig.hr_report_hour} onChange={e => setConfigField('hr_report_hour', Number(e.target.value))}>
                            {HOUR_OPTIONS.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1.5">Minute</label>
                          <select data-testid="hr-report-minute" className="input-field" value={systemConfig.hr_report_minute} onChange={e => setConfigField('hr_report_minute', Number(e.target.value))}>
                            {MINUTE_OPTIONS.map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
                          </select>
                        </div>
                      </div>
                      <div data-testid="hr-report-departments">
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                          HR report departments
                        </label>
                        <p className="text-xs text-secondary-500 dark:text-secondary-400 mb-2">
                          Leave none selected to include all departments in the HR digest.
                          Select one or more to limit who appears in the report.
                        </p>
                        {availableDepartments.length === 0 ? (
                          <p className="text-sm text-secondary-500 dark:text-secondary-400">
                            No departments found. Set departments on the Resource Utilization page first.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-2" data-testid="hr-report-departments-picker">
                            {availableDepartments.map((dept) => {
                              const active = (systemConfig.hr_report_departments || []).includes(dept);
                              return (
                                <button
                                  key={dept}
                                  type="button"
                                  data-testid={`hr-report-dept-${dept}`}
                                  onClick={() => toggleHrReportDepartment(dept)}
                                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                    active
                                      ? 'bg-primary-600 text-white border-primary-600'
                                      : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 border-secondary-300 dark:border-secondary-600'
                                  }`}
                                >
                                  {dept}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {(systemConfig.hr_report_departments || []).length > 0 && (
                          <button
                            type="button"
                            data-testid="hr-report-departments-clear"
                            className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                            onClick={() => setSystemConfig((prev) => ({
                              ...prev,
                              hr_report_departments: [],
                              hr_report_excluded_user_ids: [],
                            }))}
                          >
                            Clear selection (all departments)
                          </button>
                        )}
                        {(systemConfig.hr_report_departments || []).length > 0 && (
                          <div
                            className="mt-3 rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50/60 dark:bg-secondary-900/30 p-3"
                            data-testid="hr-report-users-picker"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                              <p className="text-xs font-medium text-secondary-700 dark:text-secondary-300">
                                People in selected departments
                              </p>
                              <div className="flex items-center gap-3">
                                <button
                                  type="button"
                                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                                  data-testid="hr-report-users-select-all"
                                  onClick={() => setAudienceUsersSelected(
                                    'hr_report_excluded_user_ids',
                                    hrReportRecipientPreview.pool,
                                    true
                                  )}
                                >
                                  Select all
                                </button>
                                <button
                                  type="button"
                                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                                  data-testid="hr-report-users-clear-all"
                                  onClick={() => setAudienceUsersSelected(
                                    'hr_report_excluded_user_ids',
                                    hrReportRecipientPreview.pool,
                                    false
                                  )}
                                >
                                  Unselect all
                                </button>
                              </div>
                            </div>
                            {hrReportRecipientPreview.pool.length === 0 ? (
                              <p className="text-xs text-secondary-500">No eligible members/leads in these departments.</p>
                            ) : (
                              <ul className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                                {hrReportRecipientPreview.pool
                                  .slice()
                                  .sort((a, b) =>
                                    String(a.full_name || a.username || a.email || '').localeCompare(
                                      String(b.full_name || b.username || b.email || '')
                                    )
                                  )
                                  .map((u) => {
                                    const checked = !(systemConfig.hr_report_excluded_user_ids || []).includes(u.id);
                                    return (
                                      <li key={u.id}>
                                        <label className="flex items-start gap-2 text-sm text-secondary-800 dark:text-secondary-200 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            className="mt-0.5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                                            data-testid={`hr-report-user-${u.id}`}
                                            checked={checked}
                                            onChange={() => toggleAudienceUser('hr_report_excluded_user_ids', u.id)}
                                          />
                                          <span className="min-w-0">
                                            <span className="font-medium">{u.full_name || u.username || u.email}</span>
                                            <span className="block text-xs text-secondary-500 truncate">
                                              {u.department}{u.email ? ` · ${u.email}` : ''}
                                            </span>
                                          </span>
                                        </label>
                                      </li>
                                    );
                                  })}
                              </ul>
                            )}
                          </div>
                        )}
                        <p
                          className="mt-2 text-xs text-secondary-600 dark:text-secondary-400"
                          data-testid="hr-report-recipient-count"
                        >
                          {hrReportRecipientPreview.filtered ? (
                            <>
                              <span className="font-medium text-secondary-800 dark:text-secondary-200">
                                {hrReportRecipientPreview.count}
                              </span>
                              {' '}
                              {hrReportRecipientPreview.count === 1 ? 'person' : 'people'} from selected
                              {' '}department{(systemConfig.hr_report_departments || []).length === 1 ? '' : 's'}
                              {' '}will appear in the HR report
                              {' '}
                              <span className="text-secondary-500">
                                (of {hrReportRecipientPreview.totalEligible} eligible members/leads)
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="font-medium text-secondary-800 dark:text-secondary-200">
                                {hrReportRecipientPreview.count}
                              </span>
                              {' '}
                              eligible members/leads across all departments will appear in the HR report
                            </>
                          )}
                        </p>
                      </div>
                      <p className="text-xs text-secondary-500" data-testid="hr-report-next-run">
                        Next HR report: {formatNextRunPreview({
                          days: [systemConfig.hr_report_day],
                          hour: systemConfig.hr_report_hour,
                          minute: systemConfig.hr_report_minute,
                        })}
                      </p>
                      <button
                        type="button"
                        data-testid="trigger-hr-report"
                        disabled={!!triggeringJob}
                        onClick={() => triggerWeeklyJob('trigger-hr-report', 'HR weekly report')}
                        className="btn-secondary text-sm"
                      >
                        {triggeringJob === 'trigger-hr-report' ? 'Triggering…' : 'Trigger HR report now'}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end pt-2">
                    <button type="submit" data-testid="system-config-save-btn" disabled={savingConfig} className="btn-primary flex items-center gap-2">
                      {savingConfig ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                      {savingConfig ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 2FA Setup Modal */}
      {showSetup2FA && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowSetup2FA(false)}>
          <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 bg-primary-100 dark:bg-primary-900/40 rounded-xl flex items-center justify-center">
                  <Shield className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Setup Two-Factor Authentication</h3>
              </div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-6">
                You will be redirected to complete the 2FA setup process.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setShowSetup2FA(false)} className="flex-1 btn-secondary">Cancel</button>
                <button onClick={() => { setShowSetup2FA(false); window.location.href = '/2fa-setup'; }} className="flex-1 btn-primary">Continue</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2FA Disable Modal */}
      {showDisable2FA && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowDisable2FA(false)}>
          <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 bg-red-100 dark:bg-red-900/40 rounded-xl flex items-center justify-center">
                  <Shield className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Disable 2FA</h3>
              </div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mb-5">
                Enter a verification code from your authenticator app to disable 2FA.
              </p>
              <form onSubmit={(e) => { e.preventDefault(); const code = e.target.verificationCode.value; if (code) handleDisable2FA(code); }} className="space-y-4">
                <input type="text" name="verificationCode" placeholder="000000" maxLength={6} className="input-field text-center text-lg font-mono tracking-widest !rounded-xl !py-3" required />
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowDisable2FA(false)} className="flex-1 btn-secondary">Cancel</button>
                  <button type="submit" disabled={loading} className="flex-1 btn-danger">{loading ? 'Disabling...' : 'Disable 2FA'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Backup Codes Modal */}
      {showBackupCodes && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setShowBackupCodes(false)}>
          <div className="bg-white dark:bg-secondary-800 rounded-2xl shadow-xl max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">Backup Codes</h3>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-3 mb-4 text-sm text-yellow-800 dark:text-yellow-200">
                Store these codes safely. They will not be shown again.
              </div>
              <div className="grid grid-cols-2 gap-2 mb-6">
                {backupCodes.map((code, index) => (
                  <div key={index} className="bg-secondary-100 dark:bg-secondary-700 p-2.5 rounded-lg text-center">
                    <code className="text-sm font-mono text-secondary-900 dark:text-secondary-100">{code}</code>
                  </div>
                ))}
              </div>
              <button onClick={() => setShowBackupCodes(false)} className="w-full btn-primary">I've Saved These Codes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
